import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, Boxes, ArrowLeftRight, FileUp, Bot, LogOut, Menu, Plus, ScanLine, Users, KeyRound } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { ScanFlash } from "@/components/ScanFlash";
import { AssistantDrawer } from "@/components/assistant/AssistantDrawer";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, id: "dashboard" },
  { to: "/articles", label: "Articles", icon: Package, id: "articles" },
  { to: "/kits", label: "Kits virtuels", icon: Boxes, id: "kits" },
  { to: "/mouvements", label: "Mouvements", icon: ArrowLeftRight, id: "mouvements" },
  { to: "/scanner", label: "Scanner", icon: ScanLine, id: "scanner" },
  { to: "/import", label: "Import / Export", icon: FileUp, id: "import" },
  { to: "/assistant", label: "Assistant IA", icon: Bot, id: "assistant" },
];

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const [pwdOpen, setPwdOpen] = useState(false);
  return (
    <div className="flex h-full flex-col bg-slate-900 text-slate-100">
      <div className="flex items-center gap-3 px-6 py-6 border-b border-slate-800">
        <div className="flex h-10 w-10 items-center justify-center rounded bg-orange-500 text-slate-950"><Boxes className="h-5 w-5" /></div>
        <div>
          <div className="font-display text-2xl font-extrabold uppercase leading-none tracking-wide" data-testid="brand-name">StockKit</div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Gestion de stock</div>
        </div>
      </div>
      <nav className="flex-1 space-y-1 px-3 py-5">
        {[...NAV, ...(user?.role === "admin" ? [{ to: "/utilisateurs", label: "Utilisateurs", icon: Users, id: "utilisateurs" }] : [])].map(({ to, label, icon: Icon, id, badge }) => (
          <NavLink key={to} to={to} end={to === "/"} onClick={onNavigate} data-testid={`nav-${id}`}
            className={({ isActive }) => `group flex h-12 items-center gap-3 rounded px-3 text-[15px] font-medium transition-colors duration-150 ${isActive ? "bg-slate-800 text-white shadow-[inset_3px_0_0_0_#f97316]" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"}`}>
            <Icon className="h-5 w-5 shrink-0" />
            <span className="flex-1">{label}</span>
            {badge && <span className="rounded bg-slate-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-slate-300">{badge}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-800 p-4">
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-700 font-display text-lg font-bold uppercase">{user?.name?.[0]}</div>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold" data-testid="current-user-name">{user?.name}</div>
            <div className="truncate text-xs text-slate-400">{user?.email}</div>
          </div>
        </div>
        <Button variant="ghost" onClick={() => setPwdOpen(true)} data-testid="change-password-button" className="h-11 w-full justify-start gap-2 text-slate-300 hover:bg-slate-800 hover:text-white">
          <KeyRound className="h-4 w-4" /> Changer mon mot de passe
        </Button>
        <Button variant="ghost" onClick={logout} data-testid="logout-button" className="h-11 w-full justify-start gap-2 text-slate-300 hover:bg-slate-800 hover:text-white">
          <LogOut className="h-4 w-4" /> Déconnexion
        </Button>
        <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <div className="flex min-h-screen">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 lg:block"><SidebarContent /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 border-0 p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarContent onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b bg-white/85 px-4 backdrop-blur-md sm:px-8">
          <Button variant="outline" size="icon" className="h-11 w-11 lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
          <div className="flex-1" />
          <Button variant="outline" className="h-11 gap-2" onClick={() => navigate("/scanner")} data-testid="topbar-scanner-button"><ScanLine className="h-4 w-4" /> Scanner</Button>
          <Button variant="outline" className="h-11 gap-2" onClick={() => navigate("/articles?new=1")} data-testid="topbar-new-article-button"><Plus className="h-4 w-4" /> Article</Button>
          <Button className="h-11 gap-2 bg-orange-500 text-slate-950 hover:bg-orange-400" onClick={() => navigate("/mouvements")} data-testid="topbar-new-movement-button"><ArrowLeftRight className="h-4 w-4" /> Mouvement</Button>
        </header>
        <main className="flex-1 px-4 py-8 sm:px-8 lg:px-10">{children}</main>
        <ScanFlash />
        <AssistantDrawer />
      </div>
    </div>
  );
}
